/**
 * PRD-RTE §TR1 — SVG-foreignObject builder for the textbox view-mode renderer.
 *
 * Produces an SVG XML string that wraps the textbox's HTML content inside a
 * `<foreignObject>` so the browser's own layout engine rasterizes the text.
 * The renderer (`textbox.renderer.ts`) then loads the SVG into an
 * `HTMLImageElement` via a `data:image/svg+xml` URL and draws it onto a
 * `Konva.Image` node — eliminating the layout divergence and lost-style bugs
 * (B5 / B6) of the legacy custom-layout pipeline.
 *
 * PRD-RTE-PARITY §TR2 — The HTML element is built by the shared factory
 * `buildTextboxHtmlElement` so the SVG view-mode and the contentEditable
 * editor mount structurally identical DOM (G1, G2). The SVG path overrides
 * the factory's logical font-size with the zoom-scaled CSS-pixel value
 * (`obj.fontSize * scale`) so the rasterized bitmap stays crisp under
 * viewport zoom; per PRD-RTE-TIPTAP-FIX §RC-A the `dpr` multiplier was
 * dropped to eliminate the bilinear-downsample softening seen in view mode.
 *
 * PRD-RTE-TIPTAP §T3.3 — The legacy raw-HTML sanitization pipeline
 * (`textbox-html-content.ts`) was deleted; inner content is now produced by
 * `generateHTML(obj.document, TEXTBOX_EXTENSIONS)` inside
 * `buildTextboxHtmlElement`, which yields TipTap-controlled markup that does
 * not require the legacy `<script>`/`<iframe>` strippers.
 */
import type { TextBox, VerticalAlign } from './textbox.interface.js';
import { buildTextboxHtmlElement, LINK_ICON_ATTR, VALIGN_LAYOUT } from './textbox-html-builder.js';

/**
 * O2D.W-PRD-030 §TR4 — scale per-run `textStyle.fontSize` marks by the viewport
 * scale on the RASTER element ONLY. The stored mark is cluster-world-units
 * (a bare level px string such as `16px`); on-screen the raster must show
 * `level × scale`. Walks every descendant carrying an inline `font-size` and
 * multiplies ONLY absolute px values, leaving the wrapper `<div>` (whose font
 * is scaled separately at the call site) untouched because `querySelectorAll`
 * excludes the root.
 *
 * px-only guard: relative-unit spans — specifically the `0.75em` verticalScript
 * wrapper span (`textbox-html-builder.ts:81`) — MUST NOT be multiplied;
 * multiplying an `em` corrupts super/subscript sizing. Applied AFTER
 * `buildTextboxHtmlElement` and BEFORE `XMLSerializer`; the separate overflow
 * MEASUREMENT element stays unscaled so `requiredHeight` is zoom-independent
 * (O2D.W-PRD-029 invariant).
 */
function scaleRasterFontSizeMarks(root: HTMLElement, scale: number): void {
  const spans = root.querySelectorAll<HTMLElement>('[style*="font-size"]');
  for (const span of Array.from(spans)) {
    const match = /^([\d.]+)px$/.exec(span.style.fontSize.trim());
    if (!match) continue; // skip relative units (em / % / rem) — px-only
    span.style.fontSize = `${parseFloat(match[1]!) * scale}px`;
  }
}

/**
 * Result of measuring whether a textbox's rendered content overflows its box
 * height (PRD-RTE-OVERFLOW §TR1). All values are in WORLD units — NOT scaled by
 * the viewport zoom and NOT DPR-multiplied — so `requiredHeight` may be passed
 * straight to `UpdatePropertyCommand(id, 'height', requiredHeight)`.
 */
/**
 * Anke, 2026-07-19 — one clickable link launcher, in WORLD units relative to the
 * textbox's own origin (the same frame the Konva group uses), so the renderer
 * can hit-test a click without knowing anything about text layout.
 *
 * Measured rather than computed: the icon sits inside browser-flowed text, so
 * its position depends on wrapping, per-run font sizes and list indents. The
 * only honest way to know where it landed is to lay the content out and read it
 * back — which the overflow pass already does anyway.
 */
export interface LinkTarget {
  readonly href: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Anke, 2026-08-15 (Bugreport) — one checklist item's real checkbox rect, in
 * WORLD units relative to the textbox's own origin, same frame as
 * `LinkTarget`.
 *
 * Measured rather than computed for the same reason as `LinkTarget`: a line's
 * height depends on the LARGEST font size among its runs (unitless
 * `line-height` inheritance, see `EDIT_TEXTBOX_LINE_HEIGHT`), so a checklist
 * whose items mix font sizes has items of genuinely different heights. The
 * previous click hit-test (`hitTestChecklistItem`, since removed along with
 * the rest of `textbox-list.helper.ts` — pre-TipTap dead code)
 * assumed one uniform `obj.fontSize * TEXTBOX_LINE_HEIGHT` for every line —
 * correct only when every item happens to share one font size. Reading the
 * real laid-out position removes that assumption entirely, the same way
 * `measureLinkIcons` already does for link launchers.
 *
 * Bugreport 2026-08-16 (user, follow-up): clicking an item's TEXT must NOT
 * toggle it — only clicking the checkbox glyph itself does (not even the
 * empty space below it, further down the row, if the item's text wraps);
 * a text click should select the textbox like any other click on it.
 * `checkboxX`/`checkboxY`/`checkboxWidth`/`checkboxHeight` are the checkbox's
 * own real laid-out rect (world units, box-relative), read the same
 * "measure, don't compute" way the row's own top used to be. (The row's own
 * top-Y itself was dropped from this shape — see `measureChecklistLines`
 * below — once the click hotspot moved to sizing itself EXACTLY to the
 * checkbox rect instead of the whole row, nothing read it any more.)
 */
export interface ChecklistLinePosition {
  readonly checkboxX: number;
  readonly checkboxY: number;
  readonly checkboxWidth: number;
  readonly checkboxHeight: number;
}

/**
 * The area one line of text occupies, in WORLD units relative to the box's
 * top-left — one rectangle per laid-out line fragment of a text run (Anke,
 * 2026-10-03: the cursor over a selected textbox shows the I-beam only over
 * text, the hand over empty space).
 */
export interface TextLineRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface OverflowMeasurement {
  /** True when the natural content height exceeds the box height. */
  readonly hasOverflow: boolean;
  /** Where text actually sits in the laid-out content (world units). */
  readonly textLines: ReadonlyArray<TextLineRect>;
  /** Clickable link launchers found in the laid-out content (world units). */
  readonly linkTargets: ReadonlyArray<LinkTarget>;
  /**
   * Real top-Y of each checklist `<li>`, in document order (world units).
   * Empty for non-checklist textboxes.
   */
  readonly checklistLines: ReadonlyArray<ChecklistLinePosition>;
  /**
   * Natural content height at the current width incl. vertical padding
   * (= el.scrollHeight after releasing the height constraint), in WORLD units.
   * Auto-expand target height.
   */
  readonly requiredHeight: number;
}

/**
 * Return value of `buildTextboxSvg` (PRD-RTE-OVERFLOW §TR2). Bundles the SVG
 * markup with the overflow flags the raster subscriber needs to drive the
 * overflow indicator's visibility and the cached auto-expand height.
 */
export interface SvgRasterResult {
  /** The SVG XML envelope (today's `buildTextboxSvg` string output). */
  readonly svg: string;
  /** Whether the rendered content overflows the box height. */
  readonly hasOverflow: boolean;
  /** World-unit natural content height (see {@link OverflowMeasurement}). */
  readonly requiredHeight: number;
  /** Clickable link launchers, world units relative to the box origin. */
  readonly linkTargets: ReadonlyArray<LinkTarget>;
  /** Real per-item checklist positions, world units (see {@link OverflowMeasurement}). */
  readonly checklistLines: ReadonlyArray<ChecklistLinePosition>;
  /** Text line areas, world units relative to the box origin. */
  readonly textLines: ReadonlyArray<TextLineRect>;
}

/**
 * Build an off-screen container sized to the box's WORLD dimensions so a child
 * element's `scrollHeight` reads directly in world units. Positioned far
 * off-screen and `visibility: hidden` so it never paints or shifts layout.
 */
function createOffscreenContainer(
  widthWu: number,
  heightWu: number,
): HTMLDivElement {
  const container = document.createElement('div');
  const cs = container.style;
  cs.position = 'absolute';
  cs.visibility = 'hidden';
  cs.left = '-99999px';
  cs.top = '0';
  cs.width = `${widthWu}px`;
  cs.height = `${heightWu}px`;
  return container;
}

/**
 * Measure whether a textbox's rendered content overflows its box height, and
 * the natural content height needed to show it all (PRD-RTE-OVERFLOW §TR1).
 *
 * The passed element MUST be a WORLD-UNIT element (a fresh
 * `buildTextboxHtmlElement(obj)` output WITHOUT the `* scale` raster font
 * override) so the read `scrollHeight` is in world units and `requiredHeight`
 * is safe to feed straight into `UpdatePropertyCommand(id, 'height', …)`.
 *
 * Critical measurement detail: `buildTextboxHtmlElement` hard-codes
 * `height: 100%` + `overflow: hidden`, on which `scrollHeight` clamps to the
 * fixed box height — a naive read would make `requiredHeight === heightWu` and
 * the indicator a silent no-op. We therefore RELEASE the height constraint
 * (`height: auto`, `overflow: visible`) while keeping the WIDTH fixed at
 * `widthWu` (so wrapping happens at the real box width) before reading. The
 * element is attached to an off-screen container sized in world units so the
 * browser performs a real layout pass; it is detached in a `finally`.
 *
 * happy-dom returns `scrollHeight === 0`, so unit tests `vi.mock` this export;
 * the real behaviour is verified by the Phase 5 Playwright E2E in Chromium.
 */
export function measureOverflow(
  el: HTMLElement,
  widthWu: number,
  heightWu: number,
  vAlign: VerticalAlign = 'top',
): OverflowMeasurement {
  const container = createOffscreenContainer(widthWu, heightWu);

  // Release the height constraint so `scrollHeight` reports the NATURAL content
  // height instead of clamping to the fixed box height. WIDTH stays fixed at
  // `widthWu` so text wraps at the real box width.
  el.style.height = 'auto';
  el.style.overflow = 'visible';
  el.style.width = `${widthWu}px`;

  container.appendChild(el);
  document.body.appendChild(container);
  try {
    const naturalHeight = el.scrollHeight;
    // With the height released, the flex `vAlign` (`justifyContent`, see
    // `buildTextboxHtmlElement`) has no free space to distribute, so the content
    // sits at the top here — while the painted raster, at its FIXED height,
    // shows it centred or at the bottom. Shift every measured rect by the
    // offset the fixed-height layout applies (Anke, 2026-10-03, review 0034/01;
    // links and checklist boxes had the same gap). Negative when the content
    // overflows: a centred/bottom flex column overflows upwards too.
    const offsetY = (heightWu - naturalHeight) * (VALIGN_LAYOUT[vAlign]?.freeSpaceAbove ?? 0);
    return {
      hasOverflow: naturalHeight > heightWu,
      requiredHeight: naturalHeight,
      // Read while the element is still attached — a detached node has no
      // layout, so every rectangle would come back as zero.
      linkTargets: measureLinkIcons(el).map((t) => ({ ...t, y: t.y + offsetY })),
      checklistLines: measureChecklistLines(el).map((l) => ({ ...l, checkboxY: l.checkboxY + offsetY })),
      textLines: measureTextLines(el).map((r) => ({ ...r, y: r.y + offsetY })),
    };
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Read the area of every laid-out line of text off the element, in WORLD units
 * relative to its top-left — same element, same origin and same "measure
 * rather than compute" rationale as `measureLinkIcons` below.
 *
 * One rectangle per line fragment of each non-blank text node
 * (`Range.getClientRects()`), so a wrapped paragraph yields one per line and
 * the space right of a short line, below the text and in the padding stays
 * outside every rectangle. Whitespace-only text nodes (the formatting between
 * tags) are skipped.
 *
 * happy-dom reports every rectangle as zero, like the measurements below; the
 * real geometry is a Chromium-only concern.
 */
function measureTextLines(el: HTMLElement): ReadonlyArray<TextLineRect> {
  const origin = el.getBoundingClientRect();
  const walker = el.ownerDocument.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = el.ownerDocument.createRange();
  const out: TextLineRect[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if ((node.textContent ?? '').trim() === '') continue;
    range.selectNodeContents(node);
    for (const r of Array.from(range.getClientRects())) {
      if (r.width === 0 || r.height === 0) continue;
      out.push({ x: r.left - origin.left, y: r.top - origin.top, width: r.width, height: r.height });
    }
  }
  return out;
}

/**
 * Read each link launcher's rectangle off the laid-out element, in WORLD units
 * relative to the element's own top-left.
 *
 * Runs on the SAME element `measureOverflow` just laid out, which matters twice
 * over: the element is world-unit sized (so the rectangles need no unscaling),
 * and its height constraint is released — meaning a launcher below the visible
 * fold is still measured. That is deliberate: it keeps this function honest
 * about what the content contains, and the renderer discards out-of-box hits
 * against the real box height, which it knows and this function does not.
 *
 * happy-dom reports every rectangle as zero, so unit tests cover the PAIRING
 * and the discard rule rather than the geometry; real coordinates are a
 * Chromium-only concern, like the overflow measurement above it.
 */
function measureLinkIcons(el: HTMLElement): ReadonlyArray<LinkTarget> {
  const icons = el.querySelectorAll<HTMLElement>(`[${LINK_ICON_ATTR}]`);
  if (icons.length === 0) return [];
  const origin = el.getBoundingClientRect();
  const out: LinkTarget[] = [];
  for (const icon of Array.from(icons)) {
    const href = icon.getAttribute(LINK_ICON_ATTR);
    if (!href) continue;
    const r = icon.getBoundingClientRect();
    out.push({
      href,
      x: r.left - origin.left,
      y: r.top - origin.top,
      width: r.width,
      height: r.height,
    });
  }
  return out;
}

/**
 * Read each VISIBLE checklist `<li>`'s real checkbox rect off the laid-out
 * element, in WORLD units relative to the element's own top-left. Mirrors
 * `measureLinkIcons` exactly — same element, same origin, same "measure
 * rather than compute" rationale. Returns `[]` for a non-checklist textbox.
 *
 * Bugreport 2026-08-16 (follow-up) — MUST skip empty `<li>` items (no
 * non-whitespace text), matching `countVisibleTaskItems`/
 * `taskItemHasVisibleContent` exactly: the returned array's index `i` is
 * used verbatim as `o2dChecklistIndex` on the click hotspot
 * (`rebuildChecklistHotspots`, textbox.renderer.ts), which
 * `toggleChecklistInDocument` (textbox-lifecycle.controller.ts) then reads
 * as "the Nth VISIBLE taskItem". Including empty items here would shift
 * every hotspot's index away from the visible-index the toggle consumer
 * expects as soon as ANY earlier item in the list is empty (e.g. a
 * newly-added item the user hasn't typed into yet) — clicking a later item
 * would then toggle the WRONG one, or silently do nothing at all.
 *
 * View-mode markup comes from `generateHTML(obj.document, ...)` (TipTap's
 * real `TaskList`/`TaskItem` extensions — `ul[data-type="taskList"] > li`),
 * NOT `buildChecklistHtml`'s legacy plain-text `<ul class="checklist">`
 * output (that path only feeds the live contentEditable's advisory
 * `obj.text` fallback). Querying `.checklist` here silently matched nothing
 * and this measurement never activated — caught by a real-Chromium check
 * (happy-dom can't lay out `<li>` elements to catch it).
 */
function measureChecklistLines(el: HTMLElement): ReadonlyArray<ChecklistLinePosition> {
  const allItems = el.querySelectorAll<HTMLElement>('ul[data-type="taskList"] > li');
  if (allItems.length === 0) return [];
  const items = Array.from(allItems).filter((item) => (item.textContent ?? '').trim() !== '');
  const origin = el.getBoundingClientRect();
  return items.map((item) => {
    const itemRect = item.getBoundingClientRect();
    const checkboxRect = item
      .querySelector<HTMLInputElement>('input[type="checkbox"]')
      ?.getBoundingClientRect();
    return {
      // Falls back to the item's own top-left / zero size on the (should
      // not happen) chance a taskItem's checkbox is missing, so a hotspot
      // still gets built (zero-size — effectively unclickable) rather than
      // throwing.
      checkboxX: (checkboxRect?.left ?? itemRect.left) - origin.left,
      checkboxY: (checkboxRect?.top ?? itemRect.top) - origin.top,
      checkboxWidth: checkboxRect?.width ?? 0,
      checkboxHeight: checkboxRect?.height ?? 0,
    };
  });
}

/**
 * Build the SVG XML envelope for one textbox and report whether its content
 * overflows the box height (PRD-RTE-OVERFLOW §TR2). The width/height attributes
 * are sized in LOGICAL CSS pixels (`obj.{width,height} * scale`) per
 * PRD-RTE-TIPTAP-FIX §RC-A candidate (a). The `dpr` parameter is currently
 * unused but retained in the signature for API stability and to allow a
 * future probe to switch to candidate (b) without a call-site change.
 *
 * PRD-RTE-PARITY-FIX §TR — the SVG covers the full object bounding box; the
 * factory's `padding: <obj.paddingV>px <obj.paddingH>px` (inside `buildTextboxHtmlElement`)
 * is the single source of truth for inset. Sizing the SVG to the inner area
 * would apply the padding twice (once via CSS, once via the Konva.Image
 * offset) and clip the last line.
 *
 * Style construction is delegated to `buildTextboxHtmlElement` (shared with
 * the editor mount path) so both renderings stay pixel-aligned. The only
 * SVG-specific deviation is the physical-pixel font-size override and the
 * XHTML `xmlns` declaration required by `<foreignObject>`.
 *
 * Zoom-independence (PRD-RTE-OVERFLOW §TR2): overflow is measured on a SEPARATE
 * WORLD-UNIT element (built BEFORE the `* scale` raster font override) so
 * `requiredHeight` stays constant in world units regardless of the zoom scale.
 */
export function buildTextboxSvg(
  obj: Readonly<TextBox>,
  scale: number,
  dpr: number,
  // Bugfix 2026-08-31 — the `<style>@font-face{...}</style>` block from
  // `board-font-embed.helper.ts`, embedded with the board font's ACTUAL bytes
  // as a `data:` URI. Optional (defaults to none) so this stays a pure,
  // synchronous builder — the caller (`textbox.renderer.ts`'s `rasterize$`)
  // resolves the async embed lookup once, up front, and hands the already-
  // computed string in here. Without it, the raster falls back to whatever
  // font the isolated `<img>` context can resolve on its own (see that
  // helper's module doc for why that is unreliable across browsers/OSes).
  fontFaceCss = '',
): SvgRasterResult {
  // PRD-RTE-TIPTAP-FIX §RC-A / T1.3 — candidate (a): size the SVG envelope
  // in LOGICAL CSS pixels (`× scale`), not physical pixels (`× scale × dpr`).
  // The legacy `× dpr` multiplier produced an oversized bitmap that Konva
  // then bilinearly downsampled into the layer canvas, softening glyphs in
  // view mode (RC-A root cause; see `analysis/T1.1-resample-probe.md`).
  // `dpr` is retained in the signature for API stability and so a future
  // probe can switch to candidate (b) without a call-site change.
  void dpr;
  const widthPx = Math.round(obj.width * scale);
  const heightPx = Math.round(obj.height * scale);
  const fontSizePx = obj.fontSize * scale;

  // Overflow is measured on a WORLD-UNIT element (no `* scale` font override)
  // so `requiredHeight` is zoom-independent (TR2). This element is consumed
  // entirely by `measureOverflow` (attached + detached there) and is distinct
  // from the scaled raster element built below.
  const measurement = measureOverflow(
    buildTextboxHtmlElement(obj),
    obj.width,
    obj.height,
    obj.vAlign,
  );

  const el = buildTextboxHtmlElement(obj);
  // Physical-pixel font-size override (DD2): the factory sets the logical
  // CSS font-size; the SVG raster path needs the device-pixel value so the
  // browser's rasterizer produces a crisp bitmap at the current zoom + DPR.
  el.style.fontSize = `${fontSizePx}px`;
  // Padding must scale with the viewport zoom just like the font: the factory
  // sets `padding: <paddingV>px <paddingH>px` in WORLD units (frozen at
  // creation time — see `TextBox.paddingV`/`paddingH`), but the raster
  // envelope is sized in `× scale` CSS px, so an unscaled world padding renders
  // at a constant on-screen inset (font grows, inset stays) — the edit overlay
  // already scales it (× scale, PRD-034/038); mirror that here so the view
  // raster keeps the padding proportional under zoom. Raster element ONLY — the
  // separate world-unit measurement element (above) stays unscaled so
  // `requiredHeight` remains zoom-independent (PRD-RTE-OVERFLOW §TR2).
  el.style.padding = `${(obj.paddingV ?? 0) * scale}px ${(obj.paddingH ?? 0) * scale}px`;
  // O2D.W-PRD-030 §TR4: per-run `textStyle.fontSize` marks are emitted verbatim
  // by `generateHTML` in cluster-world-units; scale them to `level × scale` on
  // the RASTER element only (px-only; the wrapper `div` above and the unscaled
  // measurement element are untouched) so a level-16 mark renders at 16 × scale.
  scaleRasterFontSizeMarks(el, scale);
  // `<foreignObject>` requires its child to declare the XHTML namespace so
  // user agents parse the inner tree as HTML rather than as foreign SVG.
  el.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml');

  const inner = new XMLSerializer().serializeToString(el);

  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${widthPx}" height="${heightPx}">` +
    fontFaceCss +
    `<foreignObject width="100%" height="100%">` +
    `${inner}` +
    `</foreignObject>` +
    `</svg>`;

  return {
    svg,
    hasOverflow: measurement.hasOverflow,
    requiredHeight: measurement.requiredHeight,
    linkTargets: measurement.linkTargets,
    checklistLines: measurement.checklistLines,
    textLines: measurement.textLines,
  };
}
